/**
 * Page-marker integrity helpers (frontend mirror of functions/pageIntegrity.ts).
 * Markers use the form: --- Page N ---
 * FERPA: operate on markers/counts only — never log page text.
 */

export const PAGE_MARKER_REGEX = /---\s*Page\s+(\d+)\s*---/gi;

export const PAGE_INTEGRITY_USER_MESSAGE =
  'We detected an issue while preparing this document. The processed PDF did not contain all original pages, so the download was stopped to protect document integrity. Please try again.';

export type PageIntegrityReport = {
  originalPageCount: number;
  discoveredPageNumbers: number[];
  missingPageNumbers: number[];
  duplicatePageNumbers: number[];
  unexpectedPageNumbers: number[];
  processedPageCount: number;
  isComplete: boolean;
};

export function extractPageNumbers(text: string): number[] {
  const numbers: number[] = [];
  const seen = new Set<number>();
  PAGE_MARKER_REGEX.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = PAGE_MARKER_REGEX.exec(text)) !== null) {
    const n = Number(match[1]);
    if (!Number.isFinite(n) || n < 1) continue;
    if (!seen.has(n)) {
      seen.add(n);
      numbers.push(n);
    }
  }
  return numbers.sort((a, b) => a - b);
}

export function countPageMarkers(text: string): number {
  return extractPageNumbers(text).length;
}

export function validatePageIntegrity(
  text: string,
  originalPageCount: number
): PageIntegrityReport {
  const discoveredPageNumbers = extractPageNumbers(text);
  const discoveredSet = new Set(discoveredPageNumbers);

  const missingPageNumbers: number[] = [];
  for (let page = 1; page <= originalPageCount; page++) {
    if (!discoveredSet.has(page)) missingPageNumbers.push(page);
  }

  const unexpectedPageNumbers = discoveredPageNumbers.filter(
    (page) => page < 1 || page > originalPageCount
  );

  const counts = new Map<number, number>();
  PAGE_MARKER_REGEX.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = PAGE_MARKER_REGEX.exec(text)) !== null) {
    const n = Number(match[1]);
    if (!Number.isFinite(n)) continue;
    counts.set(n, (counts.get(n) || 0) + 1);
  }
  const duplicatePageNumbers = [...counts.entries()]
    .filter(([, count]) => count > 1)
    .map(([page]) => page)
    .sort((a, b) => a - b);

  const processedPageCount = discoveredPageNumbers.filter(
    (page) => page >= 1 && page <= originalPageCount
  ).length;

  return {
    originalPageCount,
    discoveredPageNumbers,
    missingPageNumbers,
    duplicatePageNumbers,
    unexpectedPageNumbers,
    processedPageCount,
    isComplete:
      originalPageCount <= 0 ||
      (missingPageNumbers.length === 0 && unexpectedPageNumbers.length === 0),
  };
}

export function splitTextByPageMarkers(text: string): Array<{
  pageNumber: number | null;
  content: string;
}> {
  const normalized = text || '';
  const parts = normalized.split(/(?=---\s*Page\s+\d+\s*---)/i);
  const sections: Array<{ pageNumber: number | null; content: string }> = [];

  for (const part of parts) {
    if (!part.trim()) continue;
    const markerMatch = part.match(/^---\s*Page\s+(\d+)\s*---/i);
    if (markerMatch) {
      sections.push({
        pageNumber: Number(markerMatch[1]),
        content: part,
      });
    } else {
      sections.push({ pageNumber: null, content: part });
    }
  }

  if (sections.length === 0) {
    return [{ pageNumber: null, content: normalized }];
  }
  return sections;
}
